import { useEffect, useState } from "react";

const Arrow = () => <span aria-hidden="true">↗</span>;

function Brand() {
  return <a className="brand" href="#top" aria-label="Noetic home">NOETIC <i aria-hidden="true" /></a>;
}

function SignalGraphic() {
  return (
    <div className="signal-graphic" role="img" aria-label="Abstract one-minute SPY signal trace with confirmed-bar marker">
      <div className="grid" />
      <svg viewBox="0 0 620 360" aria-hidden="true">
        <path className="trace" d="M28 285 L78 268 L128 288 L178 244 L230 253 L284 192 L338 207 L392 146 L448 163 L505 87 L570 106" />
        <path className="ghost" d="M28 301 L88 297 L144 270 L205 283 L265 233 L326 241 L388 183 L452 194 L515 130 L570 142" />
        <circle cx="284" cy="192" r="7" className="watch" />
        <circle cx="570" cy="106" r="8" className="confirmed" />
      </svg>
      <div className="graphic-labels"><span>SPY · 1M</span><span>VWAP + EMA SYNC</span></div>
    </div>
  );
}

const systemSteps = [
  ["01", "ANCHORS", "Session VWAP and a configurable EMA—50 by default—define the primary directional structure."],
  ["02", "SYNC", "Price must cross both anchors in the same direction within the configured synchronization window—three one-minute bars by default."],
  ["03", "CONFIRM", "A CALL or PUT fires only on a confirmed bar close after the active filters, limits, cooldown, and reset rules pass."],
];

const faqs = [
  ["What creates a CALL or PUT?", "A CALL requires price above both session VWAP and the configured EMA after fresh, synchronized bullish crosses. A PUT uses the mirrored bearish conditions below both anchors. The directional-candle filter is on by default; optional anchor-order and expansion filters can add confirmation."],
  ["Does a PUT exit a CALL—or a CALL exit a PUT?", "No. Each is a newly confirmed directional signal. The current webhook sends buy/bullish for CALL and sell/bearish for PUT; it does not send an exit action. Any position closing, reversal, or exit behavior must be configured and verified in TradersPost and at the connected broker."],
  ["Does the webhook choose an options contract or risk settings?", "No. Its price fields contain the underlying chart close, not an option premium. Contract selection, quantity, sizing, entries, exits, stops, targets, and auto-submit behavior belong to the TradersPost strategy or subscription and the connected brokerage account."],
  ["Can a signal fire before the candle closes?", "The script evaluates signals on confirmed one-minute bars and calls TradingView alerts once per bar close. Delivery and downstream execution can still be delayed or fail, and a confirmed signal can reverse or produce no meaningful continuation."],
  ["What do I need to use it?", "Approved invite-only access on TradingView, a one-minute SPY chart, two-factor authentication for TradingView webhooks, a TradersPost strategy and subscription, a supported connected broker, and enough trading knowledge to configure and verify execution independently."],
];

const setupSteps = [
  {
    title: "Request invite-only access",
    body: (
      <>
        Message the author on TradingView and include your
        <strong> exact TradingView username</strong>. Access is granted to that
        account by the author, so confirm the spelling before sending it.
      </>
    ),
  },
  {
    title: "Open the approved account and chart",
    body: (
      <>
        Wait until the author confirms approval, then sign in to the approved
        TradingView account. Open <strong>SPY</strong> on a standard candlestick
        chart and set the timeframe to <strong>1 minute</strong>. The script
        blocks signals on other timeframes.
      </>
    ),
  },
  {
    title: "Add SPY SNIPES",
    body: (
      <>
        Select
        <strong> Indicators → Invite-only scripts → SPY SNIPES</strong> and add
        it to the chart. Invite-only access keeps the Pine source hidden and
        requires the author to approve your TradingView account before the
        script is available.
      </>
    ),
  },
  {
    title: "Review the script inputs",
    body: (
      <>
        Start with the published defaults: EMA 50, three-bar cross sync,
        directional candle required, session filter on from 9:30 a.m. through
        3:55 p.m. Eastern, five signals per day, and a one-bar cooldown. The
        anchor-order, anchor-expansion, and EMA-slope filters are off by default.
        Leave the webhook ticker set to SPY unless your verified TradersPost
        configuration requires something else.
      </>
    ),
  },
  {
    title: "Configure TradersPost",
    body: (
      <>
        Connect a supported broker in TradersPost, create an options strategy,
        and create a subscription linking that strategy to the broker account.
        Review the subscription&apos;s option selection, position sizing, allowed
        actions, order submission, and exit handling yourself. The script sends
        <strong> buy/bullish</strong> for CALL and <strong> sell/bearish</strong>
        for PUT. It does not send an exit action, contract, quantity, stop, or
        target. Confirm how TradersPost will interpret each action before use.
      </>
    ),
  },
  {
    title: "Copy the webhook URL",
    body: (
      <>
        From the TradersPost strategy dashboard, copy the strategy&apos;s complete
        webhook URL. Treat it like a password: anyone who has it may be able to
        send signals to that strategy. Never post, share, or screenshot it.
      </>
    ),
  },
  {
    title: "Create the TradingView alert",
    body: (
      <>
        On the configured SPY chart, create an alert with SPY SNIPES as the
        condition and choose <strong> Any alert() function call</strong>. In
        Notifications, enable
        <strong> Webhook URL</strong> and paste the TradersPost strategy URL.
        TradingView requires two-factor authentication for webhook alerts. Use
        the JSON message produced by the script; do not replace it with an
        unverified payload. If that condition is unavailable, stop and contact
        support before proceeding.
      </>
    ),
  },
  {
    title: "Test before live execution",
    body: (
      <>
        Trigger and observe the full workflow in a paper-trading account first.
        Confirm that TradersPost receives the signal, selects the intended
        contract, applies the expected size, and handles entries and exits as
        configured. Paper results are simulations and can differ from live
        execution.
      </>
    ),
  },
];

function SetupPage() {
  useEffect(() => {
    document.title = "SPY SNIPES Setup | Noetic Signals";
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      "Configure the invite-only SPY SNIPES indicator, TradingView webhook alerts, TradersPost, and a connected broker for paper-tested automation.",
    );
    document.querySelector('link[rel="canonical"]')?.setAttribute(
      "href",
      "https://noeticsignals.com/setup",
    );
    document.querySelector('meta[property="og:title"]')?.setAttribute(
      "content",
      "SPY SNIPES Setup | Noetic Signals",
    );
    document.querySelector('meta[property="og:description"]')?.setAttribute(
      "content",
      "Invite-only TradingView, TradersPost, and connected-broker setup for SPY SNIPES.",
    );
    document.querySelector('meta[property="og:url"]')?.setAttribute(
      "content",
      "https://noeticsignals.com/setup",
    );
  }, []);

  return (
    <>
      <a className="skip-link" href="#setup-main">
        Skip to setup instructions
      </a>
      <header className="setup-header shell">
        <a className="brand" href="/" aria-label="Noetic home">
          NOETIC <i aria-hidden="true" />
        </a>
        <a className="header-cta" href="/">
          Back to site
        </a>
      </header>
      <main className="setup-page" id="setup-main">
        <section className="setup-hero shell">
          <p className="eyebrow">
            <span /> SPY SNIPES setup
          </p>
          <h1>From signal to paper trade.</h1>
          <p className="setup-lede">
            Request invite-only SPY SNIPES access from the author, then connect
            its confirmed one-minute alerts to a TradersPost strategy carefully.
            Verify every order decision before enabling live execution.
          </p>
          <p className="publication-note">
            <strong>Invite-only</strong> keeps the Pine source code hidden and
            limits use to TradingView accounts the author has explicitly
            approved. A publication link alone does not grant access.
          </p>
          <div className="signal-key" aria-label="Signal definitions">
            <div>
              <strong>CALL</strong>
              <span>Bullish technical setup</span>
            </div>
            <div>
              <strong>PUT</strong>
              <span>Bearish technical setup</span>
            </div>
          </div>
        </section>

        <section className="setup-prerequisites shell" aria-labelledby="prerequisites-title">
          <p className="section-kicker" id="prerequisites-title">Before you start</p>
          <div className="prerequisite-grid">
            <article><h3>TradingView</h3><p>An approved invite-only account, webhook-capable plan, and two-factor authentication.</p></article>
            <article><h3>TradersPost</h3><p>An options strategy, broker-linked subscription, and a private strategy webhook URL.</p></article>
            <article><h3>Broker</h3><p>A supported connected account with paper trading available for end-to-end verification.</p></article>
          </div>
        </section>

        <section className="setup-content shell" aria-labelledby="setup-title">
          <div className="setup-heading">
            <p className="section-kicker" id="setup-title">
              Eight steps
            </p>
            <h2>Connect deliberately.</h2>
          </div>
          <ol className="setup-list">
            {setupSteps.map((step, index) => (
              <li key={step.title}>
                <span className="setup-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="setup-reference">
          <div className="shell">
            <p className="section-kicker lime">Published version reference</p>
            <div className="section-intro">
              <h2>Know what the script controls.</h2>
              <p>These defaults come from SPY SNIPES v7.4.2. Recheck them whenever Noetic publishes a new version.</p>
            </div>
            <div className="reference-grid">
              <article><h3>Chart and limits</h3><p>One-minute chart required. Session filter: 9:30–15:55. Maximum five signals per day. Cooldown: one bar.</p></article>
              <article><h3>Confirmation</h3><p>EMA length 50. VWAP/EMA cross sync: three bars. Directional candle required. Anchor-order filter off.</p></article>
              <article><h3>Expansion filters</h3><p>Expansion filter off. Fast EMA 9, one-bar expansion lookback, EMA-slope alignment off, slope lookback 3.</p></article>
              <article><h3>Webhook output</h3><p>CALL emits buy/bullish. PUT emits sell/bearish. Price fields are the underlying chart close. Default payload ticker: SPY.</p></article>
            </div>
            <p className="reset-note"><strong>Reset behavior:</strong> after a CALL, another CALL is locked until price closes below both anchors. After a PUT, another PUT is locked until price closes above both. An opposite signal is a new directional event—not an exit instruction.</p>
          </div>
        </section>

        <section className="setup-notes">
          <div className="shell setup-notes-grid">
            <div>
              <p className="section-kicker lime">Keep alerts current</p>
              <h2>Changes require a new alert.</h2>
              <p>
                TradingView saves a snapshot of the script, its inputs, and the
                chart context when an alert is created. Recreate the alert after
                changing SPY SNIPES inputs or moving to a new published version.
              </p>
            </div>
            <div className="privacy-callout">
              <strong>Keep the webhook URL private.</strong>
              <p>
                Do not send it to Noetic Signals support or include it in public
                screenshots. If it is exposed, replace it through TradersPost
                before using the strategy again.
              </p>
            </div>
          </div>
        </section>

        <section className="setup-troubleshooting shell">
          <p className="section-kicker">Troubleshooting</p>
          <h2>Trace the signal in order.</h2>
          <div className="troubleshooting-grid">
            <article>
              <h3>No SPY SNIPES signal</h3>
              <p>
                Confirm SPY, the one-minute timeframe, the enabled 9:30–15:55
                session filter, the current published script version, and its
                configured filters and limits. A setup is not guaranteed to occur.
              </p>
            </article>
            <article>
              <h3>Signal appears, alert does not</h3>
              <p>
                Check that the alert is active, uses SPY SNIPES and Any alert()
                function call, has Webhook URL enabled, and was recreated after the last
                script or input change. Review TradingView&apos;s alert log and
                webhook status.
              </p>
            </article>
            <article>
              <h3>TradersPost receives it, no order appears</h3>
              <p>
                Review the TradersPost webhook log, enabled subscription, broker
                connection, allowed ticker, market hours, buying power, contract
                filters, sizing, auto-submit, and broker rejection details.
              </p>
            </article>
          </div>
          <div className="setup-support">
            <p>
              Need help with indicator access or alert availability? Email
              <a href="mailto:support@noeticsignals.com">
                support@noeticsignals.com
              </a>
              . For broker or TradersPost order behavior, use the corresponding
              provider&apos;s support and logs.
            </p>
          </div>
        </section>

        <section className="setup-risk">
          <div className="shell">
            <strong>Trading risk</strong>
            <p>
              Options trading involves substantial risk, including loss of the
              entire amount committed to a trade. SPY SNIPES provides
              non-personalized technical signals; it does not execute trades,
              select contracts, size positions, manage exits, or guarantee
              results. You are solely responsible for all automation and trading
              decisions.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

function App() {
  const [notice, setNotice] = useState(false);
  const showNotice = () => setNotice(true);

  if (window.location.pathname === "/setup") return <SetupPage />;

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <header className="site-header shell">
        <Brand />
        <nav aria-label="Primary navigation">
          <a href="#system">System</a><a href="#automation">Automation</a><a href="#pricing">Pricing</a>
        </nav>
        <a className="header-cta" href="#pricing">View access</a>
      </header>

      <main id="main">
        <section className="hero shell" id="top">
          <div className="hero-copy">
            <p className="eyebrow"><span />A proprietary signal system by Noetic</p>
            <p className="product-label">SPY SNIPES</p>
            <h1>Confirmed SPY signals. Automation-ready.</h1>
            <p className="hero-lede">SPY SNIPES synchronizes session VWAP and EMA cross conditions on the one-minute chart, confirms at bar close, and emits structured CALL and PUT webhooks for a trader-controlled TradingView → TradersPost → broker workflow.</p>
            <div className="hero-actions">
              <a className="button button-dark" href="#pricing">Get access <Arrow /></a>
              <a className="text-link" href="#system">See how it works <span aria-hidden="true">↓</span></a>
            </div>
          </div>
          <SignalGraphic />
          <div className="hero-index" aria-hidden="true"><span>01 / SPECIALIZED</span><span>02 / SYSTEMATIC</span><span>03 / SELF-DIRECTED</span></div>
        </section>

        <section className="dark-section" id="system">
          <div className="shell">
            <p className="section-kicker lime">The system</p>
            <div className="section-intro"><h2>Two anchors. One confirmed close.</h2><p>SPY SNIPES watches price relative to session VWAP and a configurable EMA, then requires fresh cross events in the same direction before a signal can confirm.</p></div>
            <div className="step-grid">
              {systemSteps.map(([n, title, text]) => <article className="step" key={title}><p className="step-number">{n}</p><h3>{title}</h3><p>{text}</p></article>)}
            </div>
            <div className="system-note"><span>Operating scope</span><p>The one-minute timeframe is mandatory. The published defaults enable a 9:30 a.m.–3:55 p.m. Eastern session filter, a five-signal daily limit, and a one-bar cooldown. SPY is the intended chart and default webhook ticker; the script&apos;s optional SPY ticker guard is off by default.</p></div>
          </div>
        </section>

        <section className="evidence shell" id="evidence">
          <p className="section-kicker">Signal logic</p>
          <div className="section-intro"><h2>Clear conditions. Clear boundaries.</h2><p>The published script defines what a signal means. It does not supply a win rate, profit expectation, options contract, or trade-management plan.</p></div>
          <div className="evidence-grid">
            <article><span>01 / CALL</span><h3>Bullish confirmation</h3><p>Price closes above VWAP and the EMA after fresh upward crosses of both anchors. The two crosses must fall inside the sync window, and all enabled filters must pass.</p></article>
            <article><span>02 / PUT</span><h3>Bearish confirmation</h3><p>Price closes below VWAP and the EMA after fresh downward crosses of both anchors. PUT is a bearish event—not a prescribed exit from a CALL.</p></article>
            <article><span>03 / CLOSE</span><h3>Confirmed-bar timing</h3><p>Signals evaluate on a confirmed one-minute close and alert once per bar close. They can still fail, reverse, arrive during unfavorable conditions, or produce no meaningful continuation.</p></article>
          </div>
        </section>

        <section className="automation-section" id="automation">
          <div className="shell">
            <p className="section-kicker lime">Automation chain</p>
            <div className="section-intro"><h2>Each layer has one job.</h2><p>The signal and the trade are not the same thing. Execution depends on settings outside the indicator.</p></div>
            <div className="automation-grid">
              <article><span>01</span><h3>SPY SNIPES</h3><p>Evaluates the chart and emits JSON: buy/bullish for CALL, sell/bearish for PUT. Its price fields are SPY&apos;s chart close—not an option premium.</p></article>
              <article><span>02</span><h3>TradingView</h3><p>Runs the saved alert snapshot and delivers the script&apos;s webhook message to the private URL you provide.</p></article>
              <article><span>03</span><h3>TradersPost</h3><p>Processes that action under your strategy and subscription settings, including options selection, sizing, entry, exit, and auto-submit behavior.</p></article>
              <article><span>04</span><h3>Connected broker</h3><p>Accepts or rejects supported orders according to the account, broker capabilities, market conditions, and configured controls.</p></article>
            </div>
          </div>
        </section>

        <section className="fit-section shell">
          <article className="fit good"><p className="section-kicker">Built for</p><h2>Capable, self-directed traders.</h2><ul><li>Active traders working from the SPY one-minute chart</li><li>People who can configure and verify TradersPost independently</li><li>Traders seeking structured signals, not copied trades</li><li>People who understand options, automation, and execution risk</li></ul></article>
          <article className="fit bad"><p className="section-kicker">Not built for</p><h2>Beginners or copy traders.</h2><ul><li>People new to options or market risk</li><li>Anyone seeking exact contracts, sizing, stops, or targets</li><li>Anyone expecting guaranteed outcomes or effortless income</li><li>People needing individualized financial guidance</li></ul></article>
        </section>

        <section className="pricing-section" id="pricing">
          <div className="shell">
            <p className="section-kicker lime">Access</p>
            <div className="section-intro"><h2>Choose your term.</h2><p>One complete SPY SNIPES system. No free trial, diluted plan, or gated signal states.</p></div>
            <div className="pricing-grid">
              <article className="price-card"><p>Monthly</p><div className="price"><sup>$</sup><strong>397</strong><span>/ month</span></div><p>Automatically renews monthly until canceled.</p><button className="button button-light" type="button" onClick={showNotice}>Monthly access <Arrow /></button></article>
              <article className="price-card featured"><div className="value-tag">Two months free</div><p>Annual</p><div className="price"><sup>$</sup><strong>3,970</strong><span>/ year</span></div><p>Automatically renews annually until canceled.</p><button className="button button-lime" type="button" onClick={showNotice}>Annual access <Arrow /></button></article>
            </div>
            {notice && <div className="launch-notice" role="status"><strong>Checkout is not connected yet.</strong> Stripe, TradingView username validation, and access fulfillment will be enabled only after the required accounts and credentials are authorized.</div>}
            <p className="pricing-terms">Cancel at any time to prevent the next renewal. Access continues through the paid term. Purchases are final and nonrefundable except for duplicate or incorrect billing, a charge after completed cancellation, failure to provide purchased access, where required by law, or other defined billing/access exceptions. No proration.</p>
          </div>
        </section>

        <section className="faq shell">
          <p className="section-kicker">Essential questions</p><h2>Know what you are buying.</h2>
          <div className="faq-list">{faqs.map(([q, a]) => <details key={q}><summary>{q}<span aria-hidden="true">+</span></summary><p>{a}</p></details>)}</div>
        </section>
      </main>

      <footer>
        <div className="shell footer-grid"><div><Brand /><p>Proprietary market-signal systems.</p></div><div><p className="footer-product">SPY SNIPES</p><p>A proprietary signal system by Noetic.</p></div><nav aria-label="Footer navigation"><a href="#system">System</a><a href="#automation">Automation</a><a href="#pricing">Pricing</a><a href="/setup">Setup</a></nav></div>
        <div className="shell legal">SPY SNIPES is proprietary technical-analysis software for self-directed traders. It generates signals and webhook messages; TradingView delivers alerts, TradersPost processes them under customer-controlled settings, and a connected broker handles supported orders. Noetic does not manage funds, select contracts, prescribe position sizes, stops, targets, holding periods, or exits, or provide individualized investment advice. Every subscriber receives the same non-personalized signals and acts independently. Trading options and automated execution involve substantial risk. Technical signals can fail, reverse, or produce limited continuation. No result is guaranteed.</div>
      </footer>
    </>
  );
}

export default App;
